import {
  Component,
  ChangeDetectionStrategy,
  Directive,
  TemplateRef,
  computed,
  contentChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

/** Contexte passé au template de ligne. */
export interface IdraTableRowContext<T> {
  $implicit: T;
}

@Directive({ selector: '[idraTableHeader]' })
export class IdraTableHeader {
  readonly tpl = inject(TemplateRef);
}

@Directive({ selector: '[idraTableBody]' })
export class IdraTableBody<T> {
  readonly tpl = inject(TemplateRef<IdraTableRowContext<T>>);
}

@Directive({ selector: '[idraTableEmpty]' })
export class IdraTableEmpty {
  readonly tpl = inject(TemplateRef);
}

/**
 * Table simple : templates de lignes projetés, pagination client optionnelle.
 *
 * Usage :
 *  <idra-table [value]="rows()" [loading]="loading()" [paginator]="true" [rows]="10">
 *    <ng-template idraTableHeader><tr><th>…</th></tr></ng-template>
 *    <ng-template idraTableBody let-row><tr><td>{{ row.x }}</td></tr></ng-template>
 *    <ng-template idraTableEmpty><tr><td>Aucune donnée.</td></tr></ng-template>
 *  </idra-table>
 */
@Component({
  selector: 'idra-table',
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
      background: var(--idra-surface-card);
      border: 1px solid var(--idra-border);
      border-radius: var(--idra-radius-lg);
      box-shadow: var(--idra-shadow-xs);
      overflow: hidden;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13.5px;
    }

    table ::ng-deep th {
      padding: 12px 16px;
      text-align: left;
      font-size: 11.5px;
      font-weight: 800;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: var(--idra-text-muted);
      border-bottom: 1px solid var(--idra-border);
      background: var(--idra-surface-ground);
    }

    table ::ng-deep td {
      padding: 12px 16px;
      color: var(--idra-text);
      border-bottom: 1px solid var(--idra-border);
    }

    table ::ng-deep tbody tr:hover {
      background: var(--idra-surface-hover);
    }

    table ::ng-deep tbody tr:last-child td {
      border-bottom: none;
    }

    .state {
      padding: 24px 16px;
      text-align: center;
      color: var(--idra-text-muted);
    }

    .paginator {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;
      padding: 10px 16px;
      border-top: 1px solid var(--idra-border);
      font-size: 12.5px;
      color: var(--idra-text-secondary);
    }

    .paginator button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border: 1px solid var(--idra-border);
      border-radius: var(--idra-radius-xs);
      background: var(--idra-surface-card);
      color: var(--idra-text-secondary);
      cursor: pointer;
    }

    .paginator button:hover:not(:disabled) {
      background: var(--idra-surface-hover);
    }

    .paginator button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .paginator button:focus-visible {
      outline: none;
      box-shadow: var(--idra-focus-ring);
    }
  `,
  template: `
    <table>
      <thead>
        @if (header(); as h) {
          <ng-container *ngTemplateOutlet="h.tpl" />
        }
      </thead>
      <tbody>
        @if (loading()) {
          <tr>
            <td class="state" colspan="100">
              <i class="pi pi-spinner pi-spin" aria-hidden="true"></i>
              Chargement…
            </td>
          </tr>
        } @else if (value().length === 0) {
          @if (empty(); as e) {
            <ng-container *ngTemplateOutlet="e.tpl" />
          }
        } @else {
          @for (row of pagedRows(); track $index) {
            <ng-container *ngTemplateOutlet="body().tpl; context: { $implicit: row }" />
          }
        }
      </tbody>
    </table>

    @if (paginator() && !loading() && totalPages() > 1) {
      <div class="paginator">
        <span>{{ rangeLabel() }}</span>
        <button
          type="button"
          aria-label="Page précédente"
          [disabled]="currentPage() === 0"
          (click)="previous()"
        >
          <i class="pi pi-angle-left" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          aria-label="Page suivante"
          [disabled]="currentPage() >= totalPages() - 1"
          (click)="next()"
        >
          <i class="pi pi-angle-right" aria-hidden="true"></i>
        </button>
      </div>
    }
  `,
})
export class IdraTable<T> {
  readonly value = input<readonly T[]>([]);
  readonly loading = input(false);
  readonly paginator = input(false);
  readonly rows = input(10);

  protected readonly header = contentChild(IdraTableHeader);
  protected readonly body = contentChild.required(IdraTableBody<T>);
  protected readonly empty = contentChild(IdraTableEmpty);

  private readonly page = signal(0);

  protected readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.value().length / this.rows())),
  );

  /** Page effective, bornée si les données changent. */
  protected readonly currentPage = computed(() => Math.min(this.page(), this.totalPages() - 1));

  protected readonly pagedRows = computed(() => {
    if (!this.paginator()) return this.value();
    const start = this.currentPage() * this.rows();
    return this.value().slice(start, start + this.rows());
  });

  protected readonly rangeLabel = computed(() => {
    const total = this.value().length;
    const start = this.currentPage() * this.rows() + 1;
    const end = Math.min(start + this.rows() - 1, total);
    return `${start}–${end} sur ${total}`;
  });

  protected previous(): void {
    this.page.set(Math.max(0, this.currentPage() - 1));
  }

  protected next(): void {
    this.page.set(Math.min(this.totalPages() - 1, this.currentPage() + 1));
  }
}
